import { ChangeEvent, FC, useContext } from "react";
// Libraries
import { useQuery } from "@apollo/client";
import { useHistory } from "react-router";
// API
import { GET_ORGANISATIONS_LIST } from "api/Organisations/queries";
import { GetOrganizations } from "api/Organisations/types/GetOrganizations";
// Utils
import { AppContext } from "config/appProvider";
import { ANIMALS, DASHBOARD, TRADING } from "constants/Routes";
import { BU_TYPES } from "trading/constants";
import { setBUToLocalStorage, setBUTypeToLocalStorage, setFeaturesListToLocalStorage } from "helpers/storage";
import { useHasFeature, useStateSpecies } from "hooks";
// Types
import { InputSelectOptionWithTitles } from "components/Common/Field/Select";
// Components
import { InputSelect } from "components";
import { GetBusinessUnitQueryResult } from "generated/graphql";

type BusinessUnits = Array<NonNullable<GetBusinessUnitQueryResult["data"]>["businessUnit"]>;

interface Props {
  businessUnits: BusinessUnits;
}

export const DashboardBusinessUnitSelect: FC<Props> = ({ businessUnits }) => {
  const { mainAppState, changeMainAppState } = useContext(AppContext);
  const currentBusinessUnitID = mainAppState.selectedBU.toString();
  const history = useHistory();
  const { handleSetActiveSpecies, handleResetSpeciesList } = useStateSpecies();

  const isShowHomePage = useHasFeature("SHOW_HOME_PAGE");
  const isProcessor = mainAppState.selectedBUType === BU_TYPES.processor;
  const pathToRedirect = isProcessor ? TRADING : ANIMALS;

  const { data: organisations } = useQuery<GetOrganizations>(GET_ORGANISATIONS_LIST, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const userOrganisations = organisations?.organisations || [];
  const options = userOrganisations.reduce((result: InputSelectOptionWithTitles[], userOrg) => {
    result.push({
      key: userOrg?.organisation?.id ? `${userOrg?.organisation?.id}-org-id` : "key",
      label: userOrg?.organisation?.name || "",
    });
    (userOrg?.businessUnits || []).forEach((businessUnit) => {
      result.push({
        key: businessUnit?.id || "key",
        value: businessUnit?.id,
        label: businessUnit?.name || "",
      });
    });

    return result;
  }, []);

  const changeBusinessUnit = (newBusinessUnitID: string): void => {
    const newBusinessUnit = businessUnits?.find((businessUnit) => businessUnit?.id === newBusinessUnitID);
    const newBusinessUnitFoggles = newBusinessUnit?.foggles;
    const newBusinessUnitType = newBusinessUnit?.businessType?.slug;

    if (!newBusinessUnit || !newBusinessUnitFoggles || !newBusinessUnitType) return;

    setBUToLocalStorage(newBusinessUnitID);
    handleResetSpeciesList();
    handleSetActiveSpecies(undefined);

    // @ts-expect-error
    setFeaturesListToLocalStorage(newBusinessUnitFoggles);
    setBUTypeToLocalStorage(newBusinessUnitType);

    changeMainAppState({
      ...mainAppState,
      selectedBU: +newBusinessUnitID,
      selectedBUType: newBusinessUnitType,
      featuresList: newBusinessUnit?.foggles,
    });

    history.push(isShowHomePage ? DASHBOARD : pathToRedirect);
  };
  const handleChangeBusinessUnit = (event: ChangeEvent<HTMLSelectElement>) => changeBusinessUnit(event.target.value);

  return (
    <InputSelect
      name="businessUnitSwitcher"
      onChange={handleChangeBusinessUnit}
      options={options}
      value={options.length ? currentBusinessUnitID : ""}
    />
  );
};
